import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api-client";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatKes, cn } from "@/lib/utils";
import type { Product } from "@/types";

export function SearchBar({ className }: { className?: string }) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 300);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["autocomplete", debouncedQuery],
    queryFn: () =>
      api.get<{ items: Pick<Product, "id" | "name" | "slug" | "price" | "images">[] }>(
        `/products/autocomplete?q=${encodeURIComponent(debouncedQuery)}`
      ),
    enabled: debouncedQuery.trim().length >= 2,
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?q=${encodeURIComponent(query.trim())}`);
      setIsOpen(false);
    }
  }

  const results = data?.items ?? [];

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <form onSubmit={handleSubmit} role="search">
        <label htmlFor="site-search" className="sr-only">
          Search products
        </label>
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-steel-600/60" aria-hidden="true" />
          <input
            id="site-search"
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder="Search power tools, safety gear, electrical supplies…"
            className="h-11 w-full rounded-full border border-gray-300 bg-white pl-10 pr-10 text-sm text-steel placeholder:text-steel-600/50 focus-visible:outline-none"
            autoComplete="off"
          />
          {isFetching && (
            <Loader2
              className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-steel-600/60"
              aria-hidden="true"
            />
          )}
        </div>
      </form>

      {isOpen && debouncedQuery.trim().length >= 2 && (
        <div
          className="absolute left-0 top-full z-40 mt-1 w-full overflow-hidden rounded-sm border border-border bg-white shadow-plate"
          role="listbox"
        >
          {results.length === 0 && !isFetching && (
            <p className="px-4 py-3 text-sm text-steel-600">No products found for "{debouncedQuery}".</p>
          )}
          {results.map((product) => (
            <button
              key={product.id}
              role="option"
              aria-selected="false"
              onClick={() => {
                navigate(`/products/${product.slug}`);
                setIsOpen(false);
                setQuery("");
              }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-concrete-100 focus-visible:bg-concrete-100 focus-visible:outline-none"
            >
              {product.images?.[0] && (
                <img src={product.images[0].url} alt="" className="h-10 w-10 rounded-sm object-cover" />
              )}
              <span className="flex-1 truncate text-sm text-steel">{product.name}</span>
              <span className="font-mono text-xs text-steel-600">{formatKes(product.price)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}